import { useLazyQuery } from "@apollo/client/react";
import Loading from "@components/common/loading/Loading";
import {
  DSButton,
  DSCardWrapper,
} from "@components/design-system/molecules";
import { faSort } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useOrganization } from "@hooks";
import { Avatar, AvatarGroup, Tooltip } from "@mui/material";
import moment from "moment";
import { useEffect, useEffectEvent, useState } from "react";
import {
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router";

import { AllowedEmailDomainsBanner } from "./AllowedEmailDomainsBanner";
import { OrgBillingInfo } from "./BillingInfo";
import { RecordingConsentSection } from "./RecordingConsentSection";
import GET_ORGANIZATION_BY_UUID from "../../graphql/queries/GetOrganization";
import PageTitle from "../layout/PageTitle";
import { formatAddress } from "../notes/common/utils";
import { getAllowedEmailDomains } from "../team/TeamManagement/utils";
import TeamModal from "../team/TeamModal";
import { EmptyState } from "../team/TeamsManagementPage/EmptyState";

const PAGE_DESCRIPTION =
  "Your teams are listed below. Click on a team to manage its members.";

function getSortStateFromSearch(search) {
  const queryParams = new URLSearchParams(search);
  return {
    sortBy: queryParams.get("sortBy") || "",
    isSortReverse: queryParams.get("isSortReverse") === "true",
  };
}

const OrganizationPage = ({
  refetchTeams,
  loadingTeams,
  userTeams,
  userTeamsError,
}) => {
  //hooks
  const navigate = useNavigate();
  const location = useLocation();
  const { orgUuid, isOrgAdmin } = useOrganization();

  const [searchParams, setSearchParams] = useSearchParams();
  const createTeam = searchParams.has("createTeam");
  const initialSortState = getSortStateFromSearch(location.search);
  //state
  const [isSortReverse, setIsSortReverse] = useState(
    initialSortState.isSortReverse,
  );
  const [currentSort, setCurrentSort] = useState(
    initialSortState.sortBy,
  );
  const [isShowingCreateTeamModal, setIsShowingCreateTeamModal] =
    useState(createTeam);

  //queries

  // Clean up to fully use the hook in WEB-1552
  const [getOrganization, { loading, error, data, refetch }] =
    useLazyQuery(GET_ORGANIZATION_BY_UUID, {
      fetchPolicy: "no-cache",
    });

  const teams = (() => {
    // If we're able to get the org's teams, return those
    if (data?.organization?.teams?.length) {
      return data.organization.teams;
    }

    // Otherwise default to the user's teams
    return userTeams || [];
  })();

  //functions
  const hideCreateTeamModal = () => {
    const currentParams = searchParams;
    currentParams.delete("createTeam");
    setSearchParams(currentParams);
    setIsShowingCreateTeamModal(false);
  };

  const showCreateTeamModal = () => {
    setIsShowingCreateTeamModal(true);
  };

  const sortTeams = (teams, sortBy, reverse) => {
    return [...teams].sort((a, b) => {
      const aValue = a[sortBy] ?? "";
      const bValue = b[sortBy] ?? "";

      if (typeof aValue === "number" || typeof bValue === "number") {
        return reverse ? bValue - aValue : aValue - bValue;
      } else {
        return reverse
          ? bValue.localeCompare(aValue)
          : aValue.localeCompare(bValue);
      }
    });
  };

  //effects
  const getOrganizationEvent = useEffectEvent(() => {
    getOrganization({ variables: { uuid: orgUuid } });
  });

  useEffect(() => {
    if (orgUuid) {
      getOrganizationEvent();
    }
  }, [location.search, orgUuid]);

  useEffect(() => {
    if (teams) {
      const queryParams = new URLSearchParams(location.search);
      const savedScrollPosition = queryParams.get("scrollPosition");

      if (savedScrollPosition) {
        //the setTimeout seems unnecessary, but it places the scroll logic into the event queue
        //you need the call stack to clear before you can scroll so this is needed
        setTimeout(() => {
          window.scrollTo(0, parseInt(savedScrollPosition, 10));
        }, 0);
      }
    }
  }, [teams, location.search]);

  const sortedTeams = (() => {
    if (currentSort) {
      return sortTeams(teams, currentSort, isSortReverse);
    }
    return teams;
  })();

  //loading and error states
  if (loading || loadingTeams) {
    return <Loading loadingTrigger={loading || loadingTeams} />;
  }

  if (error || userTeamsError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h2>
          Error:{" "}
          {error?.message ? error.message : userTeamsError?.message}
        </h2>
        <p>Please try again later.</p>
      </div>
    );
  }

  const handleSort = (sortBy) => {
    setIsSortReverse((prevIsSortReverse) => !prevIsSortReverse);
    setCurrentSort(sortBy);
  };

  const handleRowClick = (teamUuid) => {
    const queryParams = new URLSearchParams({
      sortBy: currentSort,
      isSortReverse: isSortReverse.toString(),
      scrollPosition: window.scrollY.toString(),
    }).toString();
    navigate(`/teams/${teamUuid}?${queryParams}`);
  };

  const handleRefetch = () => {
    if (orgUuid) {
      refetch({ uuid: orgUuid });
    } else {
      refetchTeams();
    }
  };

  if (sortedTeams?.length === 0 && userTeams?.length === 0) {
    return <EmptyState />;
  }
  const hasBillingSetup = !!data?.organization?.stripeCustomerId;

  //render
  return (
    <>
      <TeamModal
        isOpen={isShowingCreateTeamModal}
        onClose={hideCreateTeamModal}
        orgUuid={orgUuid}
        orgAllowsDirectMemberAdd={
          data?.organization?.allowsDirectMemberAdd
        }
        orgIsManualBilling={data?.organization?.isManualBilling}
        hasBillingSetup={hasBillingSetup}
        isCreatingTeam={true}
        refetch={handleRefetch}
      />
      <PageTitle title="Manage Your Teams">
        <Tooltip
          arrow
          placement="left"
          title={
            !isOrgAdmin || !(teams?.length > 0 || sortedTeams?.length)
              ? "Contact support to create a new team."
              : ""
          }
        >
          <div className="flex gap-sm items-center">
            <OrgBillingInfo hasBillingSetup={hasBillingSetup} />
            <DSButton
              dataTestId="create-team-button"
              variant="primary"
              onClick={showCreateTeamModal}
              disabled={
                !isOrgAdmin ||
                !(teams?.length > 0 || sortedTeams?.length)
              }
            >
              + Create New Team
            </DSButton>
          </div>
        </Tooltip>
      </PageTitle>
      <h4 className="font-normal text-secondary mt-sm rounded-standard p-sm mb-sm">
        {PAGE_DESCRIPTION}
      </h4>

      {isOrgAdmin && (
        <AllowedEmailDomainsBanner
          domains={getAllowedEmailDomains(data?.organization)}
        />
      )}

      <div className="my-md">
        <DSCardWrapper
          variant="default"
          padding="py-sm px-sm"
          dataTestId="teams-table"
        >
          <table className="table-auto w-full text-primary text-left">
            <thead>
              <tr className="border-b border-primary">
                <TableHeader
                  title="Team Name"
                  sortBy="name"
                  handleSort={handleSort}
                />
                <TableHeader
                  title="Address"
                  sortBy="location"
                  handleSort={handleSort}
                />
                <TableHeader title="Users" />
                <TableHeader
                  title="Date Created"
                  sortBy="createdAt"
                  handleSort={handleSort}
                />
              </tr>
            </thead>
            <tbody>
              {sortedTeams?.map((team, index) => (
                <tr
                  key={index}
                  onClick={() => handleRowClick(team.uuid)}
                  className="cursor-pointer even:bg-transparent odd:bg-primary hover:bg-brand-subtle rounded-standard"
                  data-testid={`team-row-${team.name}`}
                >
                  <td className="py-sm pl-sm max-w-[200px] truncate">
                    <h5
                      className="text-sm truncate"
                      title={team.name}
                    >
                      {team.name}
                    </h5>
                  </td>
                  <td className="py-sm">
                    <h5 className="text-sm">
                      {team.location
                        ? formatAddress(team.location)
                        : "Address not specified"}
                    </h5>
                  </td>
                  <td className="py-sm">
                    <AvatarGroup
                      max={4}
                      sx={{
                        display: "flex",
                        justifyContent: "start",
                      }}
                      slotProps={{
                        additionalAvatar: {
                          style: {
                            width: 24,
                            height: 24,
                            fontSize: 10,
                          },
                        },
                      }}
                      renderSurplus={(surplus) => (
                        <Avatar
                          sx={{
                            fontSize: 12,
                          }}
                        >
                          +{surplus}
                        </Avatar>
                      )}
                    >
                      {team.users?.map((user) => (
                        <Tooltip
                          key={user.uuid}
                          arrow
                          placement="left"
                          title={`${user?.firstName}
                ${user?.lastName}`}
                        >
                          <Avatar
                            sx={{
                              width: 24,
                              height: 24,
                              fontSize: 10,
                            }}
                          >
                            {user?.firstName.charAt(0)}
                            {user?.lastName.charAt(0)}
                          </Avatar>
                        </Tooltip>
                      ))}
                    </AvatarGroup>
                  </td>
                  <td className="py-sm">
                    <h5 className="text-sm">
                      {moment(team.createdAt)?.isValid()
                        ? moment(team.createdAt).format("MMM Do YYYY")
                        : "Date unavailable"}
                    </h5>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DSCardWrapper>
      </div>
      <RecordingConsentSection />
    </>
  );
};

function TableHeader({ sortBy, title, handleSort }) {
  return (
    <th
      className="font-semibold text-sm cursor-pointer"
      onClick={sortBy ? () => handleSort(sortBy) : null}
    >
      {title}
      {sortBy && (
        <FontAwesomeIcon icon={faSort} size="xs" className="ml-1" />
      )}
    </th>
  );
}

export default OrganizationPage;
